import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CRM from './CRM';
import Clients from './Clients';
import Services from './Services';
import Tasks from './Tasks';
import Finance from './Finance';
import Settings from './Settings';
import Reports from './Reports';
import Workspace, { visibleWorkspaceModules, TileIcon, PINS_CHANGED_EVENT } from './Workspace';
import { usePermissions } from '../hooks/usePermissions';
import ChanakyaXAI from '../components/ChanakyaXAI';
import DashboardHome from '../dashboard/DashboardHome';
import { useNotifications } from '../context/NotificationsContext';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ─── ChanakyaX Logo (inline SVG — zero PNG dependency, dark mode safe) ────────
function ChanakyaXLogo({ mode = 'full', height = 38, darkMode = false }) {
  if (mode === 'icon') {
    // Collapsed sidebar — orange emblem only, no background
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="11000 0 4263 2800"
        height={height} style={{ display: 'block', width: 'auto' }} aria-label="ChanakyaX">
        <path fill="#FC5C0B" d="M13821.58 1767.69c29.82,-67.16 129.86,-166.84 176.62,-225.85l1265.19 -1541.84c-72.91,44.24 -958.38,861.93 -1046.05,951.47 -73.2,74.76 -133.38,130.95 -207.85,196.97 -115.46,102.34 -331.11,359.88 -403.04,398.54 -109.82,-138.98 -440.05,-452.63 -508.09,-566.8l-376.95 -0.81c27.87,74.49 129.25,154.75 180.19,212.36l525.64 573.92c-20.18,52.59 -238.08,304.7 -284.77,358.39 -89.85,103.32 -827.29,975.23 -858.4,1042.22l894.31 -685.24c166.19,-124.82 334.53,-406.43 445.38,-488.22 108.99,111.67 205.94,209.69 294.46,317.91 102.93,125.84 97.41,114.34 193.31,113.42 96.42,-0.92 210.43,3.99 304.07,-3.75 -32.27,-73.18 -545.13,-557.76 -594.04,-652.69z"/>
      </svg>
    );
  }
  // Full horizontal lockup — wordmark adapts to dark mode, orange stays orange
  const wordmarkColor = darkMode ? '#f1f5f9' : '#02124E';
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15263.4 3560.32"
      height={height} style={{ display: 'block', width: 'auto', maxWidth: 172 }} aria-label="ChanakyaX">
      <g>
        {/* Orange emblem — always brand orange regardless of mode */}
        <path fill="#FC5C0B" d="M13821.58 1767.69c29.82,-67.16 129.86,-166.84 176.62,-225.85l1265.19 -1541.84c-72.91,44.24 -958.38,861.93 -1046.05,951.47 -73.2,74.76 -133.38,130.95 -207.85,196.97 -115.46,102.34 -331.11,359.88 -403.04,398.54 -109.82,-138.98 -440.05,-452.63 -508.09,-566.8l-376.95 -0.81c27.87,74.49 129.25,154.75 180.19,212.36l525.64 573.92c-20.18,52.59 -238.08,304.7 -284.77,358.39 -89.85,103.32 -827.29,975.23 -858.4,1042.22l894.31 -685.24c166.19,-124.82 334.53,-406.43 445.38,-488.22 108.99,111.67 205.94,209.69 294.46,317.91 102.93,125.84 97.41,114.34 193.31,113.42 96.42,-0.92 210.43,3.99 304.07,-3.75 -32.27,-73.18 -545.13,-557.76 -594.04,-652.69z"/>
        {/* Wordmark — color driven by darkMode prop */}
        <path fill={wordmarkColor} d="M6267.05 2408.83l259.02 7.13 -3.47 -1277.96 -239.85 1.58 -8.4 890.95 -16.86 -19.07c-74.72,-55.17 -653.64,-655.92 -762.6,-768.93 -138.86,-144.03 -123.9,-147.95 -369.32,-128.47l5.46 1302.11c68.73,3.49 191.77,16.85 241.05,-9.12 32.6,-60.92 12.59,-741.84 10.3,-872.68l884.66 874.46z"/>
        <polygon fill={wordmarkColor} points="1973.31,1919.85 2787.18,1919.55 2789.06,2417.2 3052.41,2415.02 3052.05,1125.45 2788.64,1125.1 2782.22,1675.03 1971.99,1674.38 1969.16,1123.99 1708.58,1129.82 1712.66,2417.23 1970.19,2417.72"/>
        <path fill={wordmarkColor} d="M1350.08 1370.5c28.93,-41.1 106.82,-185.93 122.92,-246.2 -246.97,4.69 -809.94,-30.26 -1011.86,29.08 -586.87,172.47 -617.64,1004.21 -69,1213.44 207.71,79.21 701.49,56.65 959.06,51.46l115.82 -245.71c-258.8,-0.16 -524.78,4.7 -782.73,-1.19 -226.75,-5.18 -396.19,-136.09 -410.93,-350.47 -39.9,-580.06 603.74,-444.05 1076.71,-450.4z"/>
        <path fill={wordmarkColor} d="M7553.12 1462.41c83.47,89.42 489.4,901.38 543.34,959.24l303.59 -3.29c-22.67,-76.7 -325.16,-570 -389.75,-689.65 -67.82,-125.63 -128.68,-229.39 -196.06,-348.27 -111.53,-196.8 -123.17,-271.27 -261.57,-267.55 -128.5,3.45 -143.29,92.16 -255.3,288.54 -67.31,118.01 -127.22,221.6 -193.69,336.86 -57.14,99.08 -363.56,612.02 -382.04,678.56l295.5 0.76 535.98 -955.19z"/>
        <path fill={wordmarkColor} d="M11127.33 2419.38c307.64,0.45 285.66,31 349.1,-91.64 52.53,-101.57 447.59,-816.68 486.36,-857.52 2.39,2.67 5.45,6.42 6.89,8.26l263.55 464.85c88.71,159.81 172.38,318.09 260.87,477.95l311.25 1.16c-31.87,-100.64 -139.79,-252.36 -194.3,-353.77l-391.74 -683.03c-112.09,-189.45 -115.2,-272.65 -256.09,-269.11 -125.13,3.14 -151.35,110.39 -254.62,285.46l-394.12 680.47c-44.51,75.8 -168.52,269.27 -187.15,336.93z"/>
        <path fill={wordmarkColor} d="M3557.75 2417.64c62.19,-70.09 444.98,-834.11 543.16,-953.52 94.01,159.02 179.06,321.57 269.7,483.62l264.31 472.35 300.47 -3.46c-29.48,-85.74 -136.68,-253.64 -188.52,-341.7l-390.84 -683.98c-103.58,-172.88 -115.74,-274.6 -251.48,-275.12 -108.04,-0.42 -119.9,31.11 -165.28,110.37 -114.63,200.17 -662.8,1132.3 -670.68,1191.01l289.16 0.44z"/>
        <path fill={wordmarkColor} d="M10842.51 2074.77c-0.04,-97.1 -14.37,-112.55 36.47,-176.79 79.76,-100.78 596.47,-735.04 608.4,-773.2l-311.54 0.68 -463.6 594.6 -461.09 -595.7 -314.62 -1.81 184.17 239.83c66.21,84.11 117,143.75 178.86,225.86 58.35,77.43 123.48,160.78 183.7,235.15 106.16,131.1 93.25,91.15 93.24,307.19l3.43 291.02 261.89 -4.17 0.66 -342.68z"/>
        <path fill={wordmarkColor} d="M8601.33 2423.64l259.43 -8.84 -0.03 -649c46.99,18.06 178.25,162.93 228.46,209.5l441.53 423.84c62.04,43.21 304.78,15.89 404.37,18.81l-541.56 -509.63c-50.14,-42.1 -142.36,-121.85 -179.64,-168.55 94.49,-110.09 606.98,-577.77 636.47,-624.86 -109.55,-8.78 -302.21,-27.01 -369.2,27.67 -139.93,114.23 -515.97,533.52 -619.31,595.98l-2.18 -626.4 -258.97 -0.43 0.63 1311.9z"/>
        {/* Orange accent marks on letters */}
        <path fill="#F55C0C" d="M11809.25 2410.22c60.65,-79 85.99,-178.99 158.61,-238.89l131.75 243.68 134.65 -1.31c-20.99,-85.95 -95.34,-194.38 -132.9,-267.72 -28.92,-56.48 -96.86,-198.76 -138.57,-237.54 -43.16,54.49 -264.73,429.43 -284.18,507.24l130.64 -5.46z"/>
        <path fill="#FC610C" d="M3811.99 2412.45c147.13,8.7 122.98,19.87 182.24,-85.1 25.23,-44.7 62.35,-112.63 96.31,-154.19 40.97,26.47 128.95,203.5 145.35,242.85l141.64 -6.22 -269.27 -505.88c-47.55,31.35 -274.79,441.19 -296.26,508.53z"/>
        <path fill="#FC610C" d="M7416.67 2412.85c29.25,-68.43 99.16,-199.87 148.68,-240.16l140.32 242.37 126.98 -3.07c-45.56,-153.06 -203.91,-355.47 -264.24,-507.7l-15.79 15.2c-52.55,64.07 -130.35,186.84 -136.32,265.55l136.81 -0.95z" opacity="0.9"/>
        {/* Dot accents */}
        <path fill="#F8640A" d="M7312.89 3528.64c117.06,71.38 207.47,-28.14 142.27,-114.42 -44.93,-59.46 -185.39,-40.92 -142.27,114.42z"/>
      </g>
    </svg>
  );
}

// ─── Global Client Search ───────────────────────────────────────────────────
// Lives in the topbar so it's available from every module — matches
// Practive's "Search Clients" bar, letting a firm jump straight to a
// client's dashboard instead of opening the Clients list first.
function GlobalClientSearch({ darkMode, T }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${BASE_URL}/api/clients/search?q=${encodeURIComponent(query.trim())}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, BASE_URL]);

  const goToClient = (client) => {
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'clients', clientId: client.id } }));
    setQuery(''); setResults([]); setOpen(false);
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: 280 }}>
      <div style={{ position: 'relative' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Search Clients"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (results.length) setOpen(true); }}
          style={{
            width: '100%', border: `1px solid ${T.border}`, borderRadius: 9,
            padding: '8px 12px 8px 34px', fontSize: 13, outline: 'none',
            background: T.bg, color: T.text, boxSizing: 'border-box',
          }}
        />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, width: 340,
          background: T.bg2, border: `1px solid ${T.border2}`, borderRadius: 12,
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
          zIndex: 9999, maxHeight: 360, overflowY: 'auto',
        }}>
          {loading ? (
            <p style={{ padding: '14px 16px', fontSize: 13, color: T.text3 }}>Searching…</p>
          ) : results.length === 0 ? (
            <p style={{ padding: '14px 16px', fontSize: 13, color: T.text3 }}>
              {query.trim() ? 'No clients found.' : 'Type to search clients by name, file no., PAN, GSTIN or mobile.'}
            </p>
          ) : (
            results.map(c => (
              <button key={c.id} onClick={() => goToClient(c)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                  padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                  borderBottom: `1px solid ${T.border}`,
                }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg3}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: T.text, margin: 0 }}>{c.client_name}</p>
                  <p style={{ fontSize: 11, color: T.text3, margin: '2px 0 0' }}>
                    {c.file_number || '—'}{c.mobile ? ` · ${c.mobile}` : ''}
                  </p>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                  color: c.is_active ? '#16a34a' : '#dc2626',
                  background: c.is_active ? (darkMode ? 'rgba(16,185,129,0.15)' : '#f0fdf4') : (darkMode ? 'rgba(239,68,68,0.15)' : '#fef2f2'),
                }}>
                  {c.is_active ? 'Active' : 'Inactive'}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Shell ───────────────────────────────────────────────────────────────
function Dashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  // One-shot deep link: another page (e.g. the standalone HR workspace) can
  // set 'navOpenPage' (+ optionally 'navSection' for Settings) before routing
  // here, so the shell opens straight into that page instead of the Dashboard.
  // (Reads only, no side effect — state initializers can run twice under
  // StrictMode in dev, so the actual localStorage.removeItem happens once in
  // the effect below instead.)
  const [activePage, setActivePage] = useState(() => localStorage.getItem('navOpenPage') || 'dashboard');
  useEffect(() => { localStorage.removeItem('navOpenPage'); }, []);
  const [sidebarPinned, setSidebarPinned] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const sidebarOpen = sidebarPinned || sidebarHovered;
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('cx-theme') === 'dark');

  useEffect(() => {
    localStorage.setItem('cx-theme', darkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Theme tokens — used everywhere below
  const T = darkMode ? {
    bg:          '#0f172a',
    bg2:         '#1e293b',
    bg3:         '#334155',
    border:      '#334155',
    border2:     '#475569',
    text:        '#f1f5f9',
    text2:       '#94a3b8',
    text3:       '#64748b',
    cardBg:      '#1e293b',
    cardBorder:  '#334155',
    sidebarBg:   '#0f172a',
    sidebarBorder:'#1e293b',
    topbarBg:    '#0f172a',
    topbarBorder:'#1e293b',
    hoverBg:     'rgba(249,115,22,0.12)',
    hoverColor:  '#f97316',
    activeBg:    'linear-gradient(135deg,#0f1f45,#1a3a6b)',
    navLabel:    '#475569',
    pinBar:      '#94a3b8',
    versionBg:   'linear-gradient(135deg,#1e293b,#0f172a)',
    versionBorder:'#334155',
    inputBg:     '#1e293b',
    inputBorder: '#334155',
  } : {
    bg:          '#f1f5f9',
    bg2:         '#fff',
    bg3:         '#f8faff',
    border:      '#e8edf5',
    border2:     '#e2e8f0',
    text:        '#0f1f45',
    text2:       '#4a5568',
    text3:       '#94a3b8',
    cardBg:      '#fff',
    cardBorder:  '#f1f5f9',
    sidebarBg:   '#ffffff',
    sidebarBorder:'#e8edf5',
    topbarBg:    '#ffffff',
    topbarBorder:'#e8edf5',
    hoverBg:     'linear-gradient(135deg,#fff5eb,#ffeedd)',
    hoverColor:  '#e8620a',
    activeBg:    'linear-gradient(135deg,#0f1f45,#1a3a6b)',
    navLabel:    '#a0aec0',
    pinBar:      '#0f1f45',
    versionBg:   'linear-gradient(135deg,#f0f4ff,#fef3ec)',
    versionBorder:'#e8edf5',
    inputBg:     '#fff',
    inputBorder: '#e2e8f0',
  };

  // ── Notifications ────────────────────────────────────────────────────────
  // Polling + the new-assignment pop-up live in NotificationsProvider (App.js),
  // so they work on every page; the Dashboard only renders the bell + panel.
  const { unreadCount, notifications, bellRinging, markAllRead: markAllReadNow, markOneRead, clearAll: clearAllNow } = useNotifications();
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifToken = localStorage.getItem('token');
  const notifHeaders = { 'Authorization': `Bearer ${notifToken}` };

  // ── Cross-component navigation ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const { page, tab, section, clientId, taskId } = e.detail || {};
      if (!page) return;

      // Store extra context so target page can read it on mount
      if (tab)      localStorage.setItem('navTab',      tab);
      else          localStorage.removeItem('navTab');
      if (section)  localStorage.setItem('navSection',  section);
      else          localStorage.removeItem('navSection');
      if (clientId) localStorage.setItem('navClientId', String(clientId));
      else          localStorage.removeItem('navClientId');
      if (taskId)   localStorage.setItem('navTaskId',   String(taskId));
      else          localStorage.removeItem('navTaskId');

      setActivePage(page);
    };
    window.addEventListener('navigateTo', handler);
    return () => window.removeEventListener('navigateTo', handler);
  }, []);

  const markAllRead = async () => { await markAllReadNow(); setShowNotifPanel(false); };
  const clearAll = async () => { await clearAllNow(); setShowNotifPanel(false); };

  const handleLogout = () => {
    fetch(`${BASE}/api/auth/logout`, { method: 'POST', headers: notifHeaders }).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const { can, loading: permsLoading } = usePermissions();

  // activePage → role_permissions module key. Pages not listed (dashboard,
  // workspace) are always allowed; workspace does its own per-tile gating.
  const PAGE_MODULE = {
    crm: 'crm', clients: 'clients', services: 'services',
    tasks: 'tasks', finance: 'finance', settings: 'settings',
    reports: 'reports',
  };
  // Pages that now live in the Workspace tile grid and open in their own tab —
  // any internal shortcut (dashboard tiles, quick actions) should open the
  // tab instead of switching activePage.
  const WORKSPACE_ROUTES = { renewals: '/workspace/renewals', people: '/workspace/people', accounting: '/workspace/accounting' };
  const goToPage = (page) => {
    if (WORKSPACE_ROUTES[page]) { window.open(WORKSPACE_ROUTES[page], '_blank'); return; }
    setActivePage(page);
  };
  const SvgIcon = ({ d, d2, viewBox = '0 0 24 24', filled = false, color = 'currentColor' }) => (
    <svg width="18" height="18" viewBox={viewBox} fill={filled ? color : 'none'} stroke={filled ? 'none' : color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />{d2 && <path d={d2} />}
    </svg>
  );

  const allMenuItems = [
    { name: 'Dashboard',     path: 'dashboard', module: 'dashboard',
      icon: <SvgIcon d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" d2="M9 21V12h6v9" /> },
    { name: 'CRM',           path: 'crm',       module: 'crm',
      icon: <SvgIcon d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" d2="M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /> },
    { name: 'Clients',       path: 'clients',   module: 'clients',
      icon: <SvgIcon d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" d2="M12 11a4 4 0 100-8 4 4 0 000 8z" /> },
    { name: 'Services',      path: 'services',  module: 'services',
      icon: <SvgIcon d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /> },
    { name: 'Tasks',         path: 'tasks',     module: 'tasks',
      icon: <SvgIcon d="M9 11l3 3L22 4" d2="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /> },
    { name: 'Finance',       path: 'finance',   module: 'finance',
      icon: <SvgIcon d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /> },
    { name: 'Reports',       path: 'reports',   module: 'reports',
      icon: <SvgIcon d="M18 20V10M12 20V4M6 20v-6" /> },
    { name: 'Workspace',     path: 'workspace', module: 'workspace',
      icon: <SvgIcon d="M3 6h18M3 12h18M3 18h18" d2="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" /> },
    { name: 'Client Portal', path: 'client-portal-external', module: 'clients', external: true,
      icon: <SvgIcon d="M10 13a5 5 0 007.07 0l1.93-1.93a5 5 0 00-7.07-7.07L10.5 5.43" d2="M14 11a5 5 0 00-7.07 0l-1.93 1.93a5 5 0 007.07 7.07L13.5 18.57" /> },
    { name: 'Settings',      path: 'settings',  module: 'settings',
      icon: <SvgIcon d="M12 15a3 3 0 100-6 3 3 0 000 6z" d2="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /> },
  ];
  
  // Workspace (MCA / HR / Referrals / future paid modules) always shows in the sidebar —
  // its real access control happens per-tab, after the click (firm subscription + per-user grant).
  const menuItems = allMenuItems.filter(item => item.module === 'workspace' || can(item.module, 'view'));

  // Workspace modules this user pinned (Workspace page) — shortcuts in the sidebar on every page.
  const [pinnedKeys, setPinnedKeys] = useState([]);
  useEffect(() => {
    fetch(`${BASE}/api/dashboard-widgets/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && d.layout && d.layout.pins) setPinnedKeys(d.layout.pins); })
      .catch(() => {});
    const onChange = e => setPinnedKeys(e.detail);
    window.addEventListener(PINS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(PINS_CHANGED_EVENT, onChange);
  }, []);
  const workspaceModules = visibleWorkspaceModules(can);
  const pinnedModules = pinnedKeys.map(k => workspaceModules.find(m => m.key === k)).filter(Boolean);

  const renderContent = () => {
    // Read and clear nav context on each render
    const navTab      = localStorage.getItem('navTab');
    const navSection  = localStorage.getItem('navSection');
    const navClientId = localStorage.getItem('navClientId');
    const navTaskId   = localStorage.getItem('navTaskId');

    // Permission guard — the sidebar hides links the user can't view, but the
    // page can still be reached via dashboard tiles, quick actions or
    // cross-component navigateTo events. Block the render here too.
    const needed = PAGE_MODULE[activePage];
    if (needed && !permsLoading && !can(needed, 'view')) {
      return (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'70vh', textAlign:'center', padding:24 }}>
          <div>
            <h3 style={{ fontSize:16, fontWeight:700, color:T.text, margin:'0 0 8px' }}>
              You don't have access to this module
            </h3>
            <p style={{ fontSize:13, color:T.text3, margin:'0 0 16px' }}>
              Ask your admin to grant it under Settings → User Roles.
            </p>
            <button onClick={() => setActivePage('dashboard')} style={{
              padding:'8px 18px', borderRadius:10, border:`1px solid ${T.border2}`,
              background:T.bg2, color:T.text, fontSize:13, fontWeight:600, cursor:'pointer',
            }}>Back to Dashboard</button>
          </div>
        </div>
      );
    }

    switch (activePage) {
      case 'crm':          return <CRM     initialTab={navTab} darkMode={darkMode} T={T} />;
      case 'clients':      return <Clients darkMode={darkMode} T={T} initialClientId={navClientId} />;
      case 'services':     return <Services darkMode={darkMode} T={T} />;
      case 'tasks':        return <Tasks darkMode={darkMode} T={T} />;
      case 'finance':      return <Finance darkMode={darkMode} T={T} />;
      case 'settings':     return <Settings initialSection={localStorage.getItem('navSection')} initialGroup={localStorage.getItem('navGroup')} darkMode={darkMode} T={T} />;
      case 'reports':      return <Reports darkMode={darkMode} T={T} />;
      case 'workspace':    return <Workspace darkMode={darkMode} T={T} can={can} />;
      default:             return <DashboardHome setActivePage={goToPage} darkMode={darkMode} T={T} can={can} />;
    }
  };

  return (
    <div style={{ height: '100vh', overflow: 'hidden', background: T.bg, display: 'flex' }}>

{/* SIDEBAR */}
      <style>{`
        .sidebar-nav::-webkit-scrollbar { display: none; }
        .sb-item { transition: all 0.18s ease; }
        .sb-item:hover { background: ${T.hoverBg} !important; color: ${T.hoverColor} !important; }
        .sb-item:hover .sb-icon { color: ${T.hoverColor} !important; }
        .sb-item-active { background: ${T.activeBg} !important; color: #fff !important; box-shadow: 0 4px 14px rgba(15,31,69,0.25); }
        .sb-item-active .sb-icon { color: #f97316 !important; }
        .sb-item-mca { color: #e8620a; }
        .lead-form-scroll::-webkit-scrollbar { display: none; }
        .sb-item-mca:hover { background: ${T.hoverBg} !important; }
        .sb-pin:hover { background: ${darkMode ? '#1e293b' : '#f1f5f9'} !important; }
        body { background: ${T.bg}; }
        .lead-form-scroll::-webkit-scrollbar { display: none; }
      `}</style>
      <div
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
        className="flex flex-col fixed left-0 top-0 z-40"
        style={{
          width: sidebarOpen ? '232px' : '58px',
          height: '100vh',
          background: T.sidebarBg,
          borderRight: `1px solid ${T.sidebarBorder}`,
          boxShadow: sidebarOpen ? '4px 0 28px rgba(15,31,69,0.08)' : '2px 0 8px rgba(15,31,69,0.05)',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}>

        {/* TOP — logo & pin */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 10px', minHeight: 62,
          borderBottom: `1px solid ${T.sidebarBorder}`, flexShrink: 0,
        }}>
          {/* Collapsed: orange emblem only, centered */}
          {!sidebarOpen && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '0 4px' }}>
              <ChanakyaXLogo mode="icon" height={28} darkMode={darkMode} />
            </div>
          )}
          {/* Open: hamburger pin + full wordmark logo */}
          {sidebarOpen && (
            <>
              <button
                onClick={() => setSidebarPinned(!sidebarPinned)}
                className="sb-pin"
                style={{
                  display: 'flex', flexDirection: 'column', gap: 5, padding: '7px 6px',
                  borderRadius: 8, border: 'none', background: 'none', cursor: 'pointer', flexShrink: 0,
                }}>
                <span style={{ display: 'block', width: 18, height: 2, background: T.pinBar, borderRadius: 2 }}></span>
                <span style={{ display: 'block', width: sidebarPinned ? 11 : 18, height: 2, background: T.pinBar, borderRadius: 2, transition: 'width 0.2s' }}></span>
                <span style={{ display: 'block', width: 18, height: 2, background: T.pinBar, borderRadius: 2 }}></span>
              </button>
              <div style={{ overflow: 'hidden', display: 'flex', alignItems: 'center', flex: 1 }}>
                <ChanakyaXLogo mode="full" height={36} darkMode={darkMode} />
              </div>
            </>
          )}
        </div>

        {/* MENU */}
        <nav className="sidebar-nav" style={{
          flex: 1, padding: '10px 8px', minHeight: 0,
          overflowY: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          {/* Section label */}
          {sidebarOpen && (
            <p style={{ fontSize: 9.5, fontWeight: 700, color: T.navLabel, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 10px 8px', margin: 0 }}>
              Navigation
            </p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {menuItems.map((item) => {
              const isActive = activePage === item.path;
              const isMca = item.path === 'workspace';
              return (
                <li key={item.name}>
                  <button
                    onClick={async () => {
                      if (item.external) {
                        // No hardcoded domain — a firm's own domain (once set up)
                        // wins if present, otherwise every firm always has the
                        // /portal/<slug>/login fallback on this app's own origin
                        // (see Settings → Client Portal, client_portal_settings).
                        try {
                          const t = localStorage.getItem('token');
                          const r = await fetch(`${BASE}/api/client-portal-settings`, { headers: { Authorization: `Bearer ${t}` } });
                          const d = await r.json();
                          const wildcardBase = process.env.REACT_APP_CHANAKYAX_BASE_DOMAIN;
                          if (d?.custom_domain) {
                            window.open(`https://${d.custom_domain}/`, '_blank', 'noopener,noreferrer');
                          } else if (d?.portal_slug && wildcardBase) {
                            window.open(`https://${d.portal_slug}.${wildcardBase}/`, '_blank', 'noopener,noreferrer');
                          } else if (d?.portal_slug) {
                            window.open(`${window.location.origin}/portal/${d.portal_slug}/login`, '_blank', 'noopener,noreferrer');
                          } else {
                            alert('Set your Client Portal link first: Settings → Client Settings → Client Portal.');
                            setActivePage('settings');
                            window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'settings', section: 'client-portal' } }));
                          }
                        } catch (e) {
                          alert('Could not load the Client Portal link. Try again from Settings → Client Portal.');
                        }
                        return;
                      }
                      // Direct sidebar click — drop any stale cross-nav context
                      // so the target page opens at its default view, not wherever
                      // a previous "navigateTo" deep-link last pointed.
                      localStorage.removeItem('navTab');
                      localStorage.removeItem('navSection');
                      localStorage.removeItem('navClientId');
                      localStorage.removeItem('navTaskId');
                      setActivePage(item.path);
                    }}
                    title={!sidebarOpen ? item.name : ''}
                    className={`sb-item w-full ${isActive ? 'sb-item-active' : ''} ${isMca ? 'sb-item-mca' : ''}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 11,
                      padding: sidebarOpen ? '9px 12px' : '9px 0',
                      justifyContent: sidebarOpen ? 'flex-start' : 'center',
                      borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: isActive ? undefined : 'none',
                      color: isActive ? '#fff' : isMca ? '#e8620a' : T.text2,
                      fontSize: 13.5, fontWeight: isActive ? 600 : 500,
                      width: '100%', textAlign: 'left',
                    }}>
                    {/* Icon wrapper */}
                    <span className="sb-icon" style={{
                      flexShrink: 0,
                      color: isActive ? '#f97316' : isMca ? '#e8620a' : T.text3,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 22,
                    }}>
                      {item.icon}
                    </span>
                    {sidebarOpen && (
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', flex: 1 }}>{item.name}</span>
                    )}
                    {sidebarOpen && isMca && (
                      <svg style={{ marginLeft: 'auto', flexShrink: 0, opacity: 0.6 }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    )}
                    {sidebarOpen && isActive && !isMca && (
                      <span style={{
                        marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%',
                        background: '#f97316', flexShrink: 0,
                      }}></span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {pinnedModules.length > 0 && (
            <>
              {sidebarOpen ? (
                <p style={{ fontSize: 9.5, fontWeight: 700, color: T.navLabel, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '14px 10px 8px', margin: 0 }}>
                  Pinned
                </p>
              ) : (
                <div style={{ borderTop: `1px solid ${T.sidebarBorder}`, margin: '10px 8px' }} />
              )}
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {pinnedModules.map(m => (
                  <li key={m.key}>
                    <button
                      onClick={() => window.open(m.url, '_blank')}
                      title={`Open ${m.label} in a new tab`}
                      aria-label={`Open ${m.label}`}
                      className="sb-item w-full"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 11,
                        padding: sidebarOpen ? '9px 12px' : '9px 0',
                        justifyContent: sidebarOpen ? 'flex-start' : 'center',
                        borderRadius: 10, border: 'none', cursor: 'pointer', background: 'none',
                        color: T.text2, fontSize: 13.5, fontWeight: 500, width: '100%', textAlign: 'left',
                      }}>
                      <span className="sb-icon" style={{ flexShrink: 0, color: '#e8620a', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22 }}>
                        <TileIcon d={m.icon} size={18} />
                      </span>
                      {sidebarOpen && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{m.label}</span>}
                      {sidebarOpen && (
                        <svg style={{ marginLeft: 'auto', flexShrink: 0, opacity: 0.6 }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </nav>

        {/* BOTTOM — version badge only */}
        <div style={{ borderTop: `1px solid ${T.sidebarBorder}`, padding: '10px 8px', flexShrink: 0 }}>
          {sidebarOpen ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 10px', borderRadius: 8,
              background: T.versionBg, border: `1px solid ${T.versionBorder}`,
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
                </svg>
              </div>
              <div>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.text, margin: 0 }}>ChanakyaX ERP</p>
                <p style={{ fontSize: 10, color: T.text3, margin: 0 }}>v2.0 · Legal Chanakya</p>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'linear-gradient(135deg, #0f1f45, #1a3a6b)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ marginLeft: 58, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: T.bg, minHeight: 0 }}>
        {/* Topbar */}
        <div style={{
          background: T.topbarBg, borderBottom: `1px solid ${T.topbarBorder}`,
          padding: '0 24px', height: 56, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 30,
          boxShadow: darkMode ? '0 1px 0 rgba(255,255,255,0.05)' : '0 1px 4px rgba(15,31,69,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <h1 style={{ fontSize: 17, fontWeight: 700, color: T.text, margin: 0, whiteSpace: 'nowrap' }}>
              {menuItems.find(m => m.path === activePage)?.name || 'Dashboard'}
            </h1>
            <GlobalClientSearch darkMode={darkMode} T={T} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

            {/* Dark / Light Toggle */}
            <button
              onClick={() => setDarkMode(d => !d)}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              style={{
                width: 36, height: 36, borderRadius: 10, border: `1px solid ${T.border}`,
                background: T.bg2, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#f97316'}
              onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
            >
              {darkMode ? (
                /* Sun icon — shown in dark mode to switch to light */
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                /* Moon icon — shown in light mode to switch to dark */
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f1f45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>

            {/* Notification Bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifPanel(v => !v)}
                style={{
                  width: 36, height: 36, borderRadius: 10, border: `1px solid ${showNotifPanel?'#3b82f6':T.border}`,
                  background: showNotifPanel?'rgba(59,130,246,0.1)':T.bg2, cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', position: 'relative',
                  color: showNotifPanel?'#3b82f6':T.text3, transition:'all 0.15s',
                }}
                onMouseEnter={e=>{ if(!showNotifPanel){ e.currentTarget.style.background=T.bg3; e.currentTarget.style.color=T.text2; }}}
                onMouseLeave={e=>{ if(!showNotifPanel){ e.currentTarget.style.background=T.bg2; e.currentTarget.style.color=T.text3; }}}
                title="Notifications">
                <svg
                  width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                  style={bellRinging ? { animation:'bell-ring 0.6s ease-in-out 3' } : {}}>
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4,
                    minWidth: unreadCount > 9 ? 20 : 18,
                    height: 18, background: '#ef4444',
                    borderRadius: 9, color: '#fff',
                    fontSize: unreadCount > 99 ? 8 : 10,
                    fontWeight: 700, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', padding: '0 4px', lineHeight: 1,
                    boxShadow: '0 0 0 2px white',
                    animation: bellRinging ? 'badge-blink 0.4s ease-in-out 4' : 'none',
                  }}>
                    {unreadCount > 100 ? '100+' : unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifPanel && (
                <NotificationPanel
                  notifications={notifications}
                  unreadCount={unreadCount}
                  onMarkAllRead={markAllRead}
                  onMarkOneRead={markOneRead}
                  onClearAll={clearAll}
                  onClose={() => setShowNotifPanel(false)}
                  darkMode={darkMode}
                  T={T} />
              )}
            </div>

            <AvatarMenu user={user} onLogout={handleLogout} darkMode={darkMode} T={T} />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>{renderContent()}</div>
      </div>

      <ChanakyaXAI module={activePage} isDark={darkMode} />
    </div>
  );
}

// ─── Notification Panel (dropdown) ────────────────────────────────────────────
function NotificationPanel({ notifications, unreadCount, onMarkAllRead, onMarkOneRead, onClearAll, onClose, darkMode = false, T: theme }) {
  const { soundOn, toggleSound } = useNotifications();
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBorder:'#f1f5f9' };
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const typeIcon = (type) => {
    if (type === 'task_assignment') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/><polyline points="9 12 11 14 15 10"/></svg>
    );
    if (type === 'task_update') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
    );
    if (type === 'reminder') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
    );
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    );
  };

  const typeBg = (type) => {
    if (type === 'task_assignment') return { bg:darkMode?'rgba(59,130,246,0.15)':'#eff6ff',  border:darkMode?'rgba(59,130,246,0.3)':'#bfdbfe' };
    if (type === 'task_update')    return { bg:darkMode?'rgba(245,158,11,0.15)':'#fffbeb',  border:darkMode?'rgba(245,158,11,0.3)':'#fde68a' };
    if (type === 'reminder')       return { bg:darkMode?'rgba(139,92,246,0.15)':'#f5f3ff',  border:darkMode?'rgba(139,92,246,0.3)':'#ddd6fe' };
    return                                { bg:T.bg3,                                        border:T.border };
  };

  const readBg    = darkMode ? T.bg2  : '#fff';
  const unreadBg  = darkMode ? 'rgba(59,130,246,0.08)' : '#f8faff';
  const readHover = darkMode ? T.bg3  : '#f9fafb';
  const unreadHover = darkMode ? 'rgba(59,130,246,0.14)' : '#eff6ff';

  return (
    <div ref={panelRef} style={{
      position:'fixed', top:56, right:16,
      width:360, maxHeight:'calc(100vh - 80px)',
      display:'flex', flexDirection:'column',
      background:T.bg2, borderRadius:16,
      boxShadow: darkMode?'0 20px 60px rgba(0,0,0,0.5), 0 4px 16px rgba(0,0,0,0.3)':'0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)',
      border:`1px solid ${T.border}`, zIndex:9999, overflow:'hidden'
    }}>
      {/* Header — always navy, looks good in both modes */}
      <div style={{ padding:'14px 18px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius:'16px 16px 0 0' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeLinecap="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <span style={{ fontWeight:700, fontSize:14, color:'#fff' }}>Notifications</span>
          {unreadCount > 0 && (
            <span style={{ background:'#ef4444', color:'#fff', fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:10 }}>{unreadCount} new</span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={toggleSound} title={soundOn ? 'Alert sound is ON — click to mute' : 'Alert sound is OFF — click to turn on'} aria-label="Toggle alert sound"
            style={{ width:26, height:26, borderRadius:6, border:'1px solid rgba(255,255,255,0.2)', background: soundOn ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.06)', color: soundOn ? '#fff' : 'rgba(255,255,255,0.45)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:13, lineHeight:1 }}>
            {soundOn ? '🔊' : '🔇'}
          </button>
          {unreadCount > 0 && (
            <button onClick={onMarkAllRead}
              style={{ fontSize:12, color:'rgba(255,255,255,0.7)', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontWeight:500 }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.2)'; e.currentTarget.style.color='#fff'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.color='rgba(255,255,255,0.7)'; }}>
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={onClearAll}
              style={{ fontSize:12, color:'rgba(255,100,100,0.85)', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontWeight:500 }}
              onMouseEnter={e=>{ e.currentTarget.style.background='rgba(239,68,68,0.25)'; e.currentTarget.style.color='#fca5a5'; }}
              onMouseLeave={e=>{ e.currentTarget.style.background='rgba(239,68,68,0.15)'; e.currentTarget.style.color='rgba(255,100,100,0.85)'; }}>
              Clear all
            </button>
          )}
          <button onClick={onClose}
            style={{ width:26, height:26, borderRadius:6, border:'none', background:'rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
            onMouseEnter={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.2)'; e.currentTarget.style.color='#fff'; }}
            onMouseLeave={e=>{ e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.color='rgba(255,255,255,0.7)'; }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {/* List */}
      <div style={{ overflowY:'auto', flex:1, scrollbarWidth:'none', msOverflowStyle:'none' }}>
        {notifications.length === 0 ? (
          <div style={{ padding:'48px 16px', textAlign:'center' }}>
            <div style={{ width:48, height:48, borderRadius:12, background:T.bg3, border:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            </div>
            <p style={{ fontSize:13, fontWeight:500, color:T.text2, margin:0 }}>All caught up!</p>
            <p style={{ fontSize:12, color:T.text3, marginTop:4 }}>No notifications yet</p>
          </div>
        ) : notifications.map(n => {
          const tb = typeBg(n.type);
          return (
            <div key={n.id}
              style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}`, background:n.is_read?readBg:unreadBg, transition:'background 0.15s', cursor:'default' }}
              onMouseEnter={e=>e.currentTarget.style.background=n.is_read?readHover:unreadHover}
              onMouseLeave={e=>e.currentTarget.style.background=n.is_read?readBg:unreadBg}>
              <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                <div style={{ width:32, height:32, borderRadius:8, background:tb.bg, border:`1px solid ${tb.border}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {typeIcon(n.type)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontWeight:n.is_read?500:600, fontSize:13, color:T.text, margin:0, lineHeight:1.4 }}>{n.title}</p>
                  {n.message && <p style={{ fontSize:12, color:T.text2, margin:'3px 0 0', lineHeight:1.4 }}>{n.message}</p>}
                  <p style={{ fontSize:11, color:T.text3, margin:'5px 0 0' }}>
                    {new Date(n.created_at).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                  </p>
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6, flexShrink:0 }}>
                  {!n.is_read && (
                    <>
                      <span style={{ width:8, height:8, borderRadius:'50%', background:'#3b82f6', display:'block' }}/>
                      <button onClick={() => onMarkOneRead(n.id)}
                        style={{ fontSize:10, color:'#3b82f6', background:darkMode?'rgba(59,130,246,0.15)':'#eff6ff', border:`1px solid ${darkMode?'rgba(59,130,246,0.3)':'#bfdbfe'}`, borderRadius:5, padding:'2px 7px', cursor:'pointer', fontWeight:600, whiteSpace:'nowrap' }}
                        onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.25)':'#dbeafe'}
                        onMouseLeave={e=>e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.15)':'#eff6ff'}>
                        Mark read
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div style={{ padding:'10px 16px', borderTop:`1px solid ${T.border}`, textAlign:'center', background:T.bg3 }}>
          <span style={{ fontSize:12, color:T.text3 }}>{notifications.length} notification{notifications.length!==1?'s':''} total</span>
        </div>
      )}
    </div>
  );
}

// ─── Coming Soon ──────────────────────────────────────────────────────────────
function ComingSoon({ title, desc, icon }) {
  return (
    <div className="flex items-center justify-center h-full min-h-96">
      <div className="text-center">
        <div className="text-6xl mb-4">{icon}</div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#0f1f45' }}>{title}</h2>
        <p className="text-gray-400">{desc}</p>
        <div className="mt-4 px-4 py-2 rounded-full text-xs font-medium bg-blue-50 text-blue-600 inline-block">
          🚧 Under Development
        </div>
      </div>
    </div>
  );
}
// ─── Avatar Dropdown Menu ─────────────────────────────────────────────────────
function AvatarMenu({ user, onLogout, darkMode, T: theme }) {
  const T = theme || {
    bg2: '#fff', border: '#e2e8f0', text: '#0f1f45', text2: '#475569', text3: '#94a3b8',
    cardBg: '#fff', cardBorder: '#f1f5f9',
  };
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        }}
        title={user?.full_name}
      >
        <span className="text-sm text-gray-500 hidden md:block">{user?.full_name}</span>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: user?.avatar_url ? 'transparent' : 'linear-gradient(135deg, #1d3461, #1f6feb)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 14, fontWeight: 700, overflow: 'hidden',
          boxShadow: open ? '0 0 0 3px rgba(59,130,246,0.3)' : 'none',
          transition: 'box-shadow 0.2s',
        }}>
          {user?.avatar_url
            ? <img src={`${BASE}${user.avatar_url}`} alt={user.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 20%' }} />
            : user ? user.full_name.charAt(0).toUpperCase() : 'U'}
        </div>
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)',
          width: 220, background: T.bg2, borderRadius: 12,
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(0,0,0,0.15)',
          border: `1px solid ${T.border}`, zIndex: 200, overflow: 'hidden',
        }}>
          {/* User info header */}
          <div style={{
            padding: '14px 16px 12px',
            borderBottom: '1px solid #f3f4f6',
            background: 'linear-gradient(135deg, #060d1f, #0a1628)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: user?.avatar_url ? 'transparent' : 'linear-gradient(135deg, #1d3461, #1f6feb)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 15, fontWeight: 700, flexShrink: 0, overflow: 'hidden',
              }}>
                {user?.avatar_url
                  ? <img src={`${BASE}${user.avatar_url}`} alt={user.full_name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 20%' }} />
                  : user ? user.full_name.charAt(0).toUpperCase() : 'U'}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <p style={{ color: '#fff', fontWeight: 600, fontSize: 13, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.full_name}
                </p>
                <p style={{ color: '#93c5fd', fontSize: 11, margin: '2px 0 0', textTransform: 'capitalize' }}>
                  {user?.role}
                </p>
              </div>
            </div>
          </div>

          {/* Logout */}
          <div style={{ padding: '6px' }}>
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8, border: 'none',
                background: 'none', cursor: 'pointer', color: '#ef4444',
                fontSize: 13, fontWeight: 500, transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
export default Dashboard;